/**
 * ConversationWindow - Individual chat window for a single conversation
 *
 * DATE: 2025-12-19 | Author: IA + Luiz Couto
 *
 * Independent floating window for chatting with a specific friend.
 * Each window has its own DOM, state, and can be minimized/dragged independently.
 *
 * Part of the Multi-Window Chat System refactoring.
 */

import { ChatService, ChatMessage } from '../services/ChatService';

// ============================================================================
// TYPES
// ============================================================================

export interface WindowPosition {
  x: number;
  y: number;
}

export interface ConversationWindowConfig {
  friendId: number;
  friendNickname: string;
  isAI: boolean;
  position: WindowPosition;
  avatarUrl?: string;  // DATE: 2025-12-21 | Avatar URL for AI companions
}

// ============================================================================
// CONVERSATION WINDOW
// ============================================================================

export class ConversationWindow {
  // Friend info
  private friendId: number;
  private friendNickname: string;
  private isAI: boolean;
  private avatarUrl?: string;  // DATE: 2025-12-21 | Avatar URL

  // State
  private isMinimized: boolean = false;
  private messages: ChatMessage[] = [];
  private isLoading: boolean = true;
  private isTyping: boolean = false;  // DATE: 2025-12-21 | Typing indicator state

  // Position
  private position: WindowPosition;

  // DOM
  private container: HTMLElement | null = null;

  // Drag state
  private isDragging: boolean = false;
  private hasDragMoved: boolean = false;
  private dragStartX: number = 0;
  private dragStartY: number = 0;
  private containerStartX: number = 0;
  private containerStartY: number = 0;

  // Event handlers (bound for cleanup)
  private boundOnChatReceived: (data: any) => void;
  private boundOnMessagesLoaded: (data: any) => void;
  private boundOnFriendOnline: (data: any) => void;
  private boundOnFriendOffline: (data: any) => void;
  private boundOnTyping: (data: any) => void;  // DATE: 2025-12-21 | Typing indicator handler

  // Callbacks
  private onCloseCallback: ((friendId: number) => void) | null = null;
  private onFocusCallback: ((friendId: number) => void) | null = null;

  constructor(config: ConversationWindowConfig) {
    this.friendId = config.friendId;
    this.friendNickname = config.friendNickname;
    this.isAI = config.isAI;
    this.position = config.position;

    // DATE: 2025-12-21 | Avatar URL - use provided or generate for AIs
    // DATE: 2026-01-23 | Fix: Remove "_ia" suffix from nickname to match avatar filename
    // Avatar files are named without the _ia suffix (e.g., "dubi-avatar.jpg" not "dubi_ia-avatar.jpg")
    if (config.avatarUrl) {
      this.avatarUrl = config.avatarUrl;
    } else if (config.isAI) {
      // Generate avatar URL for AI companions - remove _ia suffix to match filename
      const avatarBaseName = config.friendNickname.toLowerCase().replace(/_ia$/, '');
      this.avatarUrl = `https://domino.netcartas.com.br/assets/avatars/${avatarBaseName}-avatar.jpg`;
    }

    // Bind event handlers for proper cleanup
    this.boundOnChatReceived = this.onChatReceived.bind(this);
    this.boundOnMessagesLoaded = this.onMessagesLoaded.bind(this);
    this.boundOnFriendOnline = this.onFriendOnline.bind(this);
    this.boundOnFriendOffline = this.onFriendOffline.bind(this);
    this.boundOnTyping = this.onTyping.bind(this);  // DATE: 2025-12-21

    console.log(`💬 [2025-12-19] ConversationWindow: Created for ${this.friendNickname} (${this.friendId})`);
  }

  // ============================================================================
  // PUBLIC API
  // ============================================================================

  /**
   * Get the friend ID for this window
   */
  public getFriendId(): number {
    return this.friendId;
  }

  /**
   * Render the window and attach to DOM
   *
   * DATE: 2025-12-20 | Author: IA + Luiz Couto
   * Redesigned to match FriendsHub quality with polished WhatsApp-style design
   */
  public render(): void {
    // Create container
    this.container = document.createElement('div');
    this.container.id = `chat-window-${this.friendId}`;
    this.container.className = `conversation-window ${this.isMinimized ? 'minimized' : ''} ${this.isAI ? 'ai-friend' : ''}`;

    // Set initial position
    this.container.style.left = `${this.position.x}px`;
    this.container.style.bottom = `${this.position.y}px`;

    // DATE: 2025-12-20 | Author: IA + Luiz Couto
    // Set initial z-index high enough to be above game chat sidebar and FriendsHub
    // This will be further updated by ChatWindowManager.handleWindowFocus() when focused
    this.container.style.zIndex = '10000';

    // Get online status
    const friend = ChatService.getInstance().getFriend(this.friendId);
    const isOnline = friend?.isOnline || this.isAI; // AI always online
    const statusText = isOnline ? 'Online' : 'Offline';
    const statusClass = isOnline ? 'online' : 'offline';

    // DATE: 2025-12-21 | Avatar: use image if available, otherwise initial
    const initial = this.friendNickname.charAt(0).toUpperCase();
    const avatarContent = this.avatarUrl
      ? `<img src="${this.avatarUrl}" alt="${this.escapeHtml(this.friendNickname)}" class="window-avatar-img" />`
      : `<div class="window-avatar-initial">${initial}</div>`;

    // Build HTML - Polished WhatsApp-style design
    this.container.innerHTML = `
      <div class="conversation-window-header" id="window-header-${this.friendId}">
        <div class="window-header-left">
          <div class="window-friend-avatar">
            ${avatarContent}
            <span class="window-status-dot ${statusClass}"></span>
          </div>
          <div class="window-friend-info">
            <div class="window-friend-name">
              ${this.escapeHtml(this.friendNickname)}
              ${this.isAI ? '<span class="window-ai-tag">IA</span>' : ''}
            </div>
            <span class="window-friend-status ${statusClass}">${statusText}</span>
          </div>
        </div>
        <div class="window-header-right">
          ${this.isAI ? `
          <button class="window-btn window-play-btn" id="play-btn-${this.friendId}" title="Jogar com ${this.escapeHtml(this.friendNickname)}">
            <span class="btn-icon">🎮</span>
          </button>
          ` : ''}
          <button class="window-btn window-minimize-btn" id="minimize-btn-${this.friendId}" title="Minimizar">
            <span class="btn-icon">−</span>
          </button>
          <button class="window-btn window-close-btn" id="close-btn-${this.friendId}" title="Fechar">
            <span class="btn-icon">×</span>
          </button>
        </div>
      </div>
      <div class="conversation-window-body" id="window-body-${this.friendId}">
        <div class="window-messages" id="messages-${this.friendId}">
          <div class="window-loading">
            <span class="window-loading-spinner">⏳</span>
            Carregando mensagens...
          </div>
        </div>
        <div class="window-input-area">
          <input
            type="text"
            class="window-input"
            id="input-${this.friendId}"
            placeholder="Digite uma mensagem..."
            autocomplete="off"
          />
          <button class="window-send-btn" id="send-btn-${this.friendId}" title="Enviar">
            <span class="send-icon">▶</span>
          </button>
        </div>
      </div>
    `;

    // Append to body
    document.body.appendChild(this.container);

    // Setup event listeners
    this.setupEventListeners();

    // Subscribe to ChatService events
    this.subscribeToEvents();

    // Request messages from server
    ChatService.getInstance().requestMessages(this.friendId);

    // Mark messages as read
    ChatService.getInstance().markAsRead(this.friendId);

    // Focus input
    setTimeout(() => {
      document.getElementById(`input-${this.friendId}`)?.focus();
    }, 100);
  }

  /**
   * Destroy the window and cleanup
   */
  public destroy(): void {
    console.log(`💬 [2025-12-19] ConversationWindow: Destroying window for ${this.friendNickname}`);

    // Unsubscribe from ChatService events
    this.unsubscribeFromEvents();

    // Remove from DOM
    if (this.container) {
      this.container.remove();
      this.container = null;
    }
  }

  /**
   * Minimize the window
   */
  public minimize(): void {
    this.isMinimized = true;
    this.container?.classList.add('minimized');
    // Body is hidden via CSS when .minimized is on container
  }

  /**
   * Maximize/restore the window
   */
  public maximize(): void {
    this.isMinimized = false;
    this.container?.classList.remove('minimized');
    // Body is shown via CSS when .minimized is removed

    // Focus input
    document.getElementById(`input-${this.friendId}`)?.focus();

    // Mark messages as read
    ChatService.getInstance().markAsRead(this.friendId);
  }

  /**
   * Toggle minimized state
   */
  public toggleMinimize(): void {
    if (this.isMinimized) {
      this.maximize();
    } else {
      this.minimize();
    }
  }

  /**
   * Focus this window (bring to front)
   * Date: 2025-12-29 | Author: IA + Luiz Couto
   * Removed auto-maximize on focus - now handled by drag click handler to avoid conflict
   */
  public focus(): void {
    if (!this.container) return;

    // Remove focus class from all windows
    document.querySelectorAll('.conversation-window').forEach(w => {
      w.classList.remove('focused');
    });

    // Add focus to this window
    this.container.classList.add('focused');

    // Notify callback
    if (this.onFocusCallback) {
      this.onFocusCallback(this.friendId);
    }
  }

  /**
   * Check if window is minimized
   */
  public isWindowMinimized(): boolean {
    return this.isMinimized;
  }

  /**
   * Get current position
   */
  public getPosition(): WindowPosition {
    return { ...this.position };
  }

  /**
   * Set position
   */
  public setPosition(x: number, y: number): void {
    this.position = { x, y };
    if (this.container) {
      this.container.style.left = `${x}px`;
      this.container.style.bottom = `${y}px`;
    }
  }

  /**
   * Set callback for when window is closed
   */
  public onClose(callback: (friendId: number) => void): void {
    this.onCloseCallback = callback;
  }

  /**
   * Set callback for when window is focused
   */
  public onFocus(callback: (friendId: number) => void): void {
    this.onFocusCallback = callback;
  }

  /**
   * Add a message to this window (for optimistic updates)
   */
  public addMessage(message: ChatMessage): void {
    this.messages.push(message);
    this.renderMessages();
  }

  // ============================================================================
  // PRIVATE - EVENT SETUP
  // ============================================================================

  /**
   * Setup DOM event listeners
   */
  private setupEventListeners(): void {
    // Close button
    document.getElementById(`close-btn-${this.friendId}`)?.addEventListener('click', () => {
      if (this.onCloseCallback) {
        this.onCloseCallback(this.friendId);
      }
    });

    // Minimize button
    document.getElementById(`minimize-btn-${this.friendId}`)?.addEventListener('click', () => {
      this.toggleMinimize();
    });

    // DATE: 2025-12-24 | Epic 1.12: Play button (only for AI friends)
    if (this.isAI) {
      document.getElementById(`play-btn-${this.friendId}`)?.addEventListener('click', () => {
        this.playWithAI();
      });
    }

    // Send button
    document.getElementById(`send-btn-${this.friendId}`)?.addEventListener('click', () => {
      this.sendMessage();
    });

    // Input enter key
    document.getElementById(`input-${this.friendId}`)?.addEventListener('keypress', (e) => {
      if (e.key === 'Enter') {
        this.sendMessage();
      }
    });

    // Click anywhere on window to focus
    this.container?.addEventListener('mousedown', () => {
      this.focus();
    });

    // Setup drag listeners
    this.setupDragListeners();
  }

  /**
   * Subscribe to ChatService events
   */
  private subscribeToEvents(): void {
    const chatService = ChatService.getInstance();
    chatService.on('chatReceived', this.boundOnChatReceived);
    chatService.on('messagesLoaded', this.boundOnMessagesLoaded);
    chatService.on('friendOnline', this.boundOnFriendOnline);
    chatService.on('friendOffline', this.boundOnFriendOffline);
    chatService.on('typing', this.boundOnTyping);  // DATE: 2025-12-21
  }

  /**
   * Unsubscribe from ChatService events
   */
  private unsubscribeFromEvents(): void {
    const chatService = ChatService.getInstance();
    chatService.off('chatReceived', this.boundOnChatReceived);
    chatService.off('messagesLoaded', this.boundOnMessagesLoaded);
    chatService.off('friendOnline', this.boundOnFriendOnline);
    chatService.off('friendOffline', this.boundOnFriendOffline);
    chatService.off('typing', this.boundOnTyping);  // DATE: 2025-12-21
  }

  // ============================================================================
  // PRIVATE - CHATSERVICE EVENT HANDLERS
  // ============================================================================

  /**
   * Handle incoming chat message
   */
  private onChatReceived(data: any): void {
    // Only handle messages for this conversation
    if (data.fromNetcartasId !== this.friendId) return;

    // DATE: 2025-12-21 | Clear typing indicator when message arrives
    this.isTyping = false;

    const newMsg: ChatMessage = {
      fromNetcartasId: data.fromNetcartasId,
      toNetcartasId: ChatService.getInstance().getMyNetcartasId()!,
      message: data.message,
      createdAt: data.createdAt || Date.now(),
      isSystem: data.isSystem
    };

    this.messages.push(newMsg);
    this.renderMessages();

    // Mark as read if window is focused and not minimized
    if (!this.isMinimized && this.container?.classList.contains('focused')) {
      ChatService.getInstance().markAsRead(this.friendId);
    } else {
      // Play notification sound if not focused
      ChatService.getInstance().playNotificationSound();
    }
  }

  /**
   * Handle typing indicator from AI companion
   * DATE: 2025-12-21 | Author: IA + Luiz Couto
   */
  private onTyping(data: any): void {
    // Only handle typing for this conversation
    if (data.fromNetcartasId !== this.friendId) return;

    console.log(`💬 [2025-12-21] ConversationWindow: Typing indicator from ${data.fromNickname}: ${data.isTyping}`);

    this.isTyping = data.isTyping;
    this.renderMessages();
  }

  /**
   * Handle messages loaded from server
   */
  private onMessagesLoaded(data: any): void {
    // Only handle messages for this conversation
    if (data.friendNetcartasId !== this.friendId) return;

    console.log(`💬 [2025-12-19] ConversationWindow: Messages loaded for ${this.friendNickname}`);

    this.isLoading = false;
    this.messages = data.messages || [];
    this.renderMessages();
  }

  /**
   * Handle friend coming online
   */
  private onFriendOnline(data: any): void {
    if (data.netcartasId !== this.friendId) return;
    this.updateOnlineStatus(true);
  }

  /**
   * Handle friend going offline
   */
  private onFriendOffline(data: any): void {
    if (data.netcartasId !== this.friendId) return;
    // AI companions never go offline
    if (!this.isAI) {
      this.updateOnlineStatus(false);
    }
  }

  // ============================================================================
  // PRIVATE - RENDERING
  // ============================================================================

  /**
   * Render messages in the container
   *
   * DATE: 2025-12-20 | Author: IA + Luiz Couto
   * Updated to use correct CSS classes for polished design
   */
  private renderMessages(): void {
    const container = document.getElementById(`messages-${this.friendId}`);
    if (!container) return;

    if (this.isLoading) {
      container.innerHTML = `
        <div class="window-loading">
          <span class="window-loading-spinner">⏳</span>
          Carregando mensagens...
        </div>
      `;
      return;
    }

    if (this.messages.length === 0) {
      container.innerHTML = `
        <div class="window-empty-messages">
          <span class="empty-icon">💬</span>
          <span class="empty-text">Nenhuma mensagem ainda.</span>
          <span class="empty-hint">Diga oi para ${this.escapeHtml(this.friendNickname)}!</span>
        </div>
      `;
      return;
    }

    const myId = ChatService.getInstance().getMyNetcartasId();

    // DATE: 2025-12-21 | Build messages HTML
    let messagesHtml = this.messages.map(msg => {
      const time = new Date(msg.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

      // System messages (like "Amizade aceita")
      if (msg.isSystem) {
        return `
          <div class="window-message system">
            <div class="window-system-bubble">
              <span class="window-message-text">${this.escapeHtml(msg.message)}</span>
            </div>
          </div>
        `;
      }

      const isMine = msg.fromNetcartasId === myId;
      // DATE: 2025-12-24 | Epic 1.12: Format message with game invites as buttons
      const formattedMessage = this.formatMessageWithGameInvites(msg.message);
      return `
        <div class="window-message ${isMine ? 'mine' : 'theirs'}">
          <div class="window-message-bubble">
            <span class="window-message-text">${formattedMessage}</span>
            <span class="window-message-time">${time}</span>
          </div>
        </div>
      `;
    }).join('');

    // DATE: 2025-12-21 | Add typing indicator if AI is typing
    if (this.isTyping) {
      messagesHtml += `
        <div class="window-message theirs">
          <div class="window-message-bubble window-typing-indicator">
            <span class="typing-dot"></span>
            <span class="typing-dot"></span>
            <span class="typing-dot"></span>
          </div>
        </div>
      `;
    }

    container.innerHTML = messagesHtml;

    // DATE: 2025-12-24 | Epic 1.12: Attach event listeners to game invite buttons
    // DATE: 2025-12-26 | Include aiNetcartasId for proper AI selection
    container.querySelectorAll('.game-invite-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const target = e.currentTarget as HTMLElement;
        const gameId = target.dataset.gameId;
        const gameType = target.dataset.gameType;
        const aiNetcartasId = target.dataset.aiId; // Optional AI ID from data-ai-id attribute
        if (gameId && gameType) {
          this.navigateToGameFromInvite(gameId, gameType, aiNetcartasId);
        }
      });
    });

    // Scroll to bottom
    container.scrollTop = container.scrollHeight;
  }

  /**
   * Update online status display
   *
   * DATE: 2025-12-20 | Author: IA + Luiz Couto
   * Updated to use correct CSS classes
   */
  private updateOnlineStatus(isOnline: boolean): void {
    // Update status text
    const statusTextEl = this.container?.querySelector('.window-friend-status');
    if (statusTextEl) {
      statusTextEl.textContent = isOnline ? 'Online' : 'Offline';
      statusTextEl.className = `window-friend-status ${isOnline ? 'online' : 'offline'}`;
    }

    // Update status dot
    const statusDotEl = this.container?.querySelector('.window-status-dot');
    if (statusDotEl) {
      statusDotEl.className = `window-status-dot ${isOnline ? 'online' : 'offline'}`;
    }
  }

  // ============================================================================
  // PRIVATE - ACTIONS
  // ============================================================================

  /**
   * Send a message
   */
  private sendMessage(): void {
    const input = document.getElementById(`input-${this.friendId}`) as HTMLInputElement;
    const message = input?.value.trim();

    if (!message) return;

    console.log(`💬 [2025-12-19] ConversationWindow: Sending message to ${this.friendNickname}`);

    // Send via ChatService
    ChatService.getInstance().sendMessage(this.friendId, message, this.isAI);

    // Optimistic update - add message to UI immediately
    const newMsg: ChatMessage = {
      fromNetcartasId: ChatService.getInstance().getMyNetcartasId()!,
      toNetcartasId: this.friendId,
      message,
      createdAt: Date.now()
    };
    this.messages.push(newMsg);
    this.renderMessages();

    // Clear input
    input.value = '';
    input.focus();
  }

  /**
   * Play with this AI Companion
   *
   * DATE: 2025-12-24 | Author: IA + Luiz Couto
   * Epic 1.12: Play with AI Companion Feature
   *
   * Creates a game with this specific AI Companion and navigates to it.
   * Uses the /api/lobby/create-game-with-ai endpoint.
   */
  private async playWithAI(): Promise<void> {
    console.log(`🎮 [2025-12-24] ConversationWindow: Playing with ${this.friendNickname} (${this.friendId})`);

    // Disable play button while loading
    const playBtn = document.getElementById(`play-btn-${this.friendId}`) as HTMLButtonElement;
    if (playBtn) {
      playBtn.disabled = true;
      playBtn.innerHTML = '<span class="btn-icon">⏳</span>';
    }

    try {
      // Get server URL from ChatService
      const serverUrl = ChatService.getInstance().getServerUrl();

      // Call the create-game-with-ai endpoint
      const response = await fetch(`${serverUrl}/api/lobby/create-game-with-ai`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          aiNetcartasId: this.friendId,
          gameType: 'individual_2p'  // Default to 1v1 for private chat
        })
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.status}`);
      }

      const result = await response.json() as {
        success: boolean;
        gameId: string;
        gameUrl: string;
        gameType: string;
      };

      if (result.success && result.gameUrl) {
        console.log(`✅ [2025-12-24] ConversationWindow: Game created - ${result.gameId}`);

        // Navigate to the game
        // The gameUrl is a relative path like /game/private-ai-XXX?type=individual_2p
        // We dispatch an event that will be handled by the main app to navigate
        // DATE: 2025-12-24 | Fixed: dispatch to document (main.ts listens on document)
        const navigateEvent = new CustomEvent('navigateToGame', {
          detail: {
            gameId: result.gameId,
            gameType: result.gameType,  // Include gameType for main.ts
            gameUrl: result.gameUrl,
            aiName: this.friendNickname
          }
        });
        document.dispatchEvent(navigateEvent);

        // Minimize this chat window
        this.minimize();
      } else {
        throw new Error('Game creation failed');
      }

    } catch (error) {
      console.error(`❌ [2025-12-24] ConversationWindow: Error playing with AI:`, error);

      // Show error to user
      alert(`Não foi possível criar o jogo. Tente novamente.`);

    } finally {
      // Re-enable play button
      if (playBtn) {
        playBtn.disabled = false;
        playBtn.innerHTML = '<span class="btn-icon">🎮</span>';
      }
    }
  }

  // ============================================================================
  // PRIVATE - DRAG FUNCTIONALITY
  // ============================================================================

  /**
   * Setup drag listeners for the header
   */
  private setupDragListeners(): void {
    const header = document.getElementById(`window-header-${this.friendId}`);
    if (!header || !this.container) return;

    // Mouse events
    header.addEventListener('mousedown', (e) => this.startDrag(e));
    document.addEventListener('mousemove', (e) => this.onDrag(e));
    document.addEventListener('mouseup', () => this.stopDrag());

    // Touch events for mobile
    header.addEventListener('touchstart', (e) => this.startDragTouch(e), { passive: false });
    document.addEventListener('touchmove', (e) => this.onDragTouch(e), { passive: false });
    document.addEventListener('touchend', () => this.stopDrag());
  }

  private startDrag(e: MouseEvent): void {
    // Don't start drag if clicking a button
    if ((e.target as HTMLElement).closest('button')) return;

    this.isDragging = true;
    this.hasDragMoved = false;
    this.dragStartX = e.clientX;
    this.dragStartY = e.clientY;

    if (this.container) {
      const rect = this.container.getBoundingClientRect();
      this.containerStartX = rect.left;
      this.containerStartY = window.innerHeight - rect.bottom;
      this.container.classList.add('dragging');
    }

    e.preventDefault();
  }

  private startDragTouch(e: TouchEvent): void {
    if ((e.target as HTMLElement).closest('button')) return;
    if (e.touches.length !== 1) return;

    this.isDragging = true;
    this.hasDragMoved = false;
    this.dragStartX = e.touches[0].clientX;
    this.dragStartY = e.touches[0].clientY;

    if (this.container) {
      const rect = this.container.getBoundingClientRect();
      this.containerStartX = rect.left;
      this.containerStartY = window.innerHeight - rect.bottom;
      this.container.classList.add('dragging');
    }

    e.preventDefault();
  }

  private onDrag(e: MouseEvent): void {
    if (!this.isDragging || !this.container) return;

    const deltaX = e.clientX - this.dragStartX;
    const deltaY = e.clientY - this.dragStartY;

    // Only consider it a drag if moved more than 5px
    const DRAG_THRESHOLD = 5;
    if (Math.abs(deltaX) > DRAG_THRESHOLD || Math.abs(deltaY) > DRAG_THRESHOLD) {
      this.hasDragMoved = true;
    }

    this.updatePosition(deltaX, deltaY);
  }

  private onDragTouch(e: TouchEvent): void {
    if (!this.isDragging || !this.container || e.touches.length !== 1) return;

    const deltaX = e.touches[0].clientX - this.dragStartX;
    const deltaY = e.touches[0].clientY - this.dragStartY;

    const DRAG_THRESHOLD = 5;
    if (Math.abs(deltaX) > DRAG_THRESHOLD || Math.abs(deltaY) > DRAG_THRESHOLD) {
      this.hasDragMoved = true;
    }

    this.updatePosition(deltaX, deltaY);
    e.preventDefault();
  }

  private updatePosition(deltaX: number, deltaY: number): void {
    if (!this.container) return;

    // Calculate new position (note: bottom increases as you move up)
    let newLeft = this.containerStartX + deltaX;
    let newBottom = this.containerStartY - deltaY;

    // Constrain to viewport
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const containerWidth = this.container.offsetWidth;

    const minVisible = 100;
    newLeft = Math.max(-containerWidth + minVisible, Math.min(newLeft, viewportWidth - minVisible));
    newBottom = Math.max(0, Math.min(newBottom, viewportHeight - 48));

    this.container.style.left = `${newLeft}px`;
    this.container.style.bottom = `${newBottom}px`;
    this.container.style.right = 'auto';

    // Update stored position
    this.position = { x: newLeft, y: newBottom };
  }

  private stopDrag(): void {
    if (this.isDragging && this.container) {
      const wasActualDrag = this.hasDragMoved;

      this.isDragging = false;
      this.container.classList.remove('dragging');

      // If no movement, treat as click - toggle minimize
      if (!wasActualDrag) {
        this.toggleMinimize();
      }
    }
  }

  // ============================================================================
  // PRIVATE - HELPERS
  // ============================================================================

  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Format message text, converting game invite markup to clickable buttons
   *
   * DATE: 2025-12-24 | Author: IA + Luiz Couto
   * Epic 1.12: Play with AI Companion Feature
   *
   * DATE: 2025-12-26 | Updated format to include AI's netcartasId
   * Detects [GAME_INVITE:gameId:gameType:aiNetcartasId] pattern and replaces with HTML button
   *
   * @param message - Raw message text
   * @returns HTML with game invites as buttons
   */
  private formatMessageWithGameInvites(message: string): string {
    // First escape HTML to prevent XSS
    const escaped = this.escapeHtml(message);

    // Pattern: [GAME_INVITE:gameId:gameType:aiNetcartasId] (aiNetcartasId is optional for backwards compat)
    // Example: [GAME_INVITE:individual_2p-042:individual_2p:900001]
    const inviteRegex = /\[GAME_INVITE:([^:]+):([^:\]]+)(?::(\d+))?\]/g;

    // Replace with clickable button (includes aiNetcartasId in data attribute)
    return escaped.replace(inviteRegex, (_match, gameId, gameType, aiNetcartasId) => {
      const gameTypeLabel = gameType === 'partnerships_4p' ? 'Duplas 2v2' : '1v1';
      const aiIdAttr = aiNetcartasId ? ` data-ai-id="${aiNetcartasId}"` : '';
      return `
        <button class="game-invite-btn" data-game-id="${gameId}" data-game-type="${gameType}"${aiIdAttr}>
          <span class="invite-icon">🎮</span>
          <span class="invite-text">Entrar na Partida (${gameTypeLabel})</span>
        </button>
      `;
    });
  }

  /**
   * Navigate to a game from a chat invite
   *
   * DATE: 2025-12-24 | Author: IA + Luiz Couto
   * Epic 1.12: Play with AI Companion Feature
   *
   * DATE: 2025-12-26 | Added aiNetcartasId parameter for proper AI selection
   *
   * @param gameId - The game ID to join
   * @param gameType - The game type
   * @param aiNetcartasId - Optional AI's netcartas ID (for selecting correct AI)
   */
  private navigateToGameFromInvite(gameId: string, gameType: string, aiNetcartasId?: string): void {
    console.log(`🎮 [2025-12-26] ConversationWindow: Navigating to game ${gameId} from invite (aiNetcartasId: ${aiNetcartasId || 'none'})`);

    // Build game URL with aiNetcartasId if available
    let gameUrl = `/game/${gameId}?type=${gameType}`;
    if (aiNetcartasId) {
      gameUrl += `&ai=${aiNetcartasId}`;
    }

    // Dispatch navigation event to document (main.ts listens on document)
    // DATE: 2025-12-24 | Fixed: was window.dispatchEvent, but listener is on document
    const navigateEvent = new CustomEvent('navigateToGame', {
      detail: {
        gameId,
        gameType,  // Include gameType for main.ts
        gameUrl,
        aiName: this.friendNickname,
        aiNetcartasId  // DATE: 2025-12-26 | Include AI ID for proper selection
      }
    });
    document.dispatchEvent(navigateEvent);

    // Minimize this chat window
    this.minimize();
  }
}
